/* iter460 — Lactation Coach Page (Phase 1 + 2).
 *
 * Self-identified module for breastfeeding mothers, hidden by default until
 * the user actively sets a lactation status in this page (visibility = A,
 * per product spec).  Integrates with:
 *   • POST/GET /api/lactation/profile
 *   • GET /api/lactation/targets
 *   • POST /api/lactation/food-eval
 *   • GET /api/lactation/mastitis-checklist
 *   • GET /api/lactation/status-options
 *
 * Layout:
 *   • Persona form (status + comorbidities + body metrics)
 *   • Daily nutrient compass (kcal, protein, DHA, water, fiber, lecithin)
 *   • Mastitis mode (toggle + checklist + red-flag banner when fever > 38.5°C)
 *   • Galactogogue food picks (educational, food-carrier-only per regulatory
 *     stance; no supplement dosing UI)
 *   • Floating Lactobot button (Phase 3) — wires to Unified AI Assistant
 *     when `bot: "lactobot"` is selected
 *
 * NOT a medical device.  All UI shows persistent disclaimer.
 */
import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { toast } from "sonner";
import {
  Baby, Heart, Droplets, Flame, Salad, Brain, ShieldCheck,
  AlertTriangle, Sparkles, Save, Loader2, ChevronRight, ChevronDown,
  Activity, BookOpen, Phone,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import LactobotFloatingButton from "@/components/lactation/LactobotFloatingButton";

const TX = {
  bg: {
    title: "Mama Coach",
    subtitle: "Educational — НЕ е медицинско изделие. Винаги консултирай педиатъра.",
    persona_title: "Твоят статус",
    status_label: "В момента съм:",
    comorbidities_label: "Здравословни особености (по желание):",
    body_label: "Тегло и височина (по желание, използват се за персонализация):",
    weight_kg: "Тегло сега (kg)",
    weight_pre: "Тегло преди бременност (kg)",
    height_cm: "Височина (cm)",
    breastfeeds: "Брой кърмения на ден",
    save: "Запази",
    saved: "Запазено!",
    targets_title: "Дневна хранителна цел",
    targets_disclaimer: "Общи educational цели. Не са медицинска препоръка.",
    mastitis_title: "Мастит режим",
    mastitis_inactive: "Нямам активен мастит",
    mastitis_active_label: "Имам активен мастит",
    mastitis_fever: "Температура (°C)",
    mastitis_redness: "Зачервяване (cm)",
    mastitis_hours: "Часове от началото",
    red_flag_now: "⚠️ Иди при лекар СЕГА",
    red_flag_explain: "Базирано на симптомите ти, нашата система счита, че се нуждаеш от спешна медицинска консултация.",
    call_112: "Обади се на 112",
    checklist_title: "Anti-mastitis дневен план",
    food_picks_title: "Препоръчителни храни-носители",
    galactogogue_section: "Подкрепят лактация",
    lecithin_section: "Богати на лецитин (мастит-приятелски)",
    anti_inflam_section: "Анти-възпалителни",
    caution_section: "Избягвай или ограничи",
    save_profile_first: "Първо избери статус и натисни Запази",
  },
  en: {
    title: "Mama Coach",
    subtitle: "Educational — NOT a medical device. Always consult your paediatrician.",
    persona_title: "Your status",
    status_label: "Right now I am:",
    comorbidities_label: "Health considerations (optional):",
    body_label: "Body metrics (optional, used for personalization):",
    weight_kg: "Current weight (kg)",
    weight_pre: "Pre-pregnancy weight (kg)",
    height_cm: "Height (cm)",
    breastfeeds: "Daily breastfeeds",
    save: "Save",
    saved: "Saved!",
    targets_title: "Daily nutrition target",
    targets_disclaimer: "Educational only. Not medical advice.",
    mastitis_title: "Mastitis mode",
    mastitis_inactive: "No active mastitis",
    mastitis_active_label: "I have active mastitis",
    mastitis_fever: "Fever (°C)",
    mastitis_redness: "Redness (cm)",
    mastitis_hours: "Hours since onset",
    red_flag_now: "⚠️ See a doctor NOW",
    red_flag_explain: "Based on your symptoms, our system recommends urgent medical consultation.",
    call_112: "Call 112",
    checklist_title: "Anti-mastitis daily plan",
    food_picks_title: "Recommended food carriers",
    galactogogue_section: "Support lactation",
    lecithin_section: "Lecithin-rich (mastitis-friendly)",
    anti_inflam_section: "Anti-inflammatory",
    caution_section: "Avoid or limit",
    save_profile_first: "Pick a status and Save first",
  },
  es: {
    title: "Coach de Lactancia",
    subtitle: "Educativo — NO es un dispositivo médico. Consulte siempre a su pediatra.",
    persona_title: "Tu estado",
    status_label: "Ahora mismo soy:",
    comorbidities_label: "Consideraciones de salud (opcional):",
    body_label: "Métricas corporales (opcional, utilizadas para personalización):",
    weight_kg: "Peso actual (kg)",
    weight_pre: "Peso antes del embarazo (kg)",
    height_cm: "Altura (cm)",
    breastfeeds: "Tomas de pecho diarias",
    save: "Guardar",
    saved: "¡Guardado!",
    targets_title: "Objetivo nutricional diario",
    targets_disclaimer: "Solo educativo. No es consejo médico.",
    mastitis_title: "Modo mastitis",
    mastitis_inactive: "Sin mastitis activa",
    mastitis_active_label: "Tengo mastitis activa",
    mastitis_fever: "Fiebre (°C)",
    mastitis_redness: "Enrojecimiento (cm)",
    mastitis_hours: "Horas desde el inicio",
    red_flag_now: "⚠️ Consulte a un médico AHORA",
    red_flag_explain: "Basándose en sus síntomas, nuestro sistema recomienda una consulta médica urgente.",
    call_112: "Llame al 112",
    checklist_title: "Plan diario anti-mastitis",
    food_picks_title: "Alimentos recomendados",
    galactogogue_section: "Apoyar la lactancia",
    lecithin_section: "Ricos en lecitina (favorables para la mastitis)",
    anti_inflam_section: "Antiinflamatorios",
    caution_section: "Evitar o limitar",
    save_profile_first: "Seleccione un estado y guarde primero",
  },
};

const FOOD_CATALOG = {
  galactogogue: [
    { name_bg: "Овесена каша", name_en: "Oatmeal", tier: "strong", note_bg: "β-glucan стимулира пролактин", note_en: "β-glucan stimulates prolactin" },
    { name_bg: "Ленено семе", name_en: "Flaxseed", tier: "moderate", note_bg: "ALA omega-3 + лигнани", note_en: "ALA omega-3 + lignans" },
    { name_bg: "Копър (семена)", name_en: "Fennel seeds", tier: "moderate", note_bg: "Анетол — традиционно използван", note_en: "Anethole — traditional use" },
    { name_bg: "Ечемик", name_en: "Barley", tier: "moderate", note_bg: "β-glucan source", note_en: "β-glucan source" },
    { name_bg: "Бадеми", name_en: "Almonds", tier: "weak", note_bg: "Здравословни мазнини + калций", note_en: "Healthy fats + calcium" },
    { name_bg: "Moringa", name_en: "Moringa", tier: "moderate", note_bg: "Полифеноли, EBM умерена", note_en: "Polyphenols, moderate EBM" },
  ],
  lecithin: [
    { name_bg: "Жълтък (1 яйце)", name_en: "Egg yolk (1 egg)", mg: "≈ 250mg" },
    { name_bg: "Слънчогледови семки (30g)", name_en: "Sunflower seeds (30g)", mg: "≈ 450mg" },
    { name_bg: "Соя/тофу (100g)", name_en: "Soy/tofu (100g)", mg: "≈ 2000mg" },
    { name_bg: "Пшеничен зародиш (20g)", name_en: "Wheat germ (20g)", mg: "≈ 600mg" },
  ],
  anti_inflam: [
    { name_bg: "Сьомга", name_en: "Salmon" },
    { name_bg: "Орехи", name_en: "Walnuts" },
    { name_bg: "Боровинки", name_en: "Blueberries" },
    { name_bg: "Спанак", name_en: "Spinach" },
    { name_bg: "Зелен чай", name_en: "Green tea" },
    { name_bg: "Зехтин extra virgin", name_en: "Extra-virgin olive oil" },
    { name_bg: "Чиа семена", name_en: "Chia seeds" },
    { name_bg: "Джинджифил", name_en: "Ginger" },
  ],
  caution: [
    { name_bg: "Алкохол", name_en: "Alcohol", note_bg: "Преминава в кърма — 2-3ч преди хранене", note_en: "Passes to milk — 2-3h before feed" },
    { name_bg: "Кафе/кофеин", name_en: "Coffee/caffeine", note_bg: "Лимит 300mg/ден", note_en: "Limit 300mg/day" },
    { name_bg: "Мента", name_en: "Peppermint", note_bg: "Висока доза → ↓ лактация", note_en: "High dose → ↓ supply" },
    { name_bg: "Магданоз (висока доза)", name_en: "Parsley (high dose)", note_bg: "Висока доза → ↓ лактация", note_en: "High dose → ↓ supply" },
    { name_bg: "Градински чай", name_en: "Sage", note_bg: "Висока доза → ↓ лактация", note_en: "High dose → ↓ supply" },
  ],
};

const TIER_COLOR = {
  strong:   "#10B981", // emerald
  moderate: "#0EA5E9", // sky
  weak:     "#94A3B8", // slate
};

export default function LactationCoachPage() {
  const { user, API } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const lang = language === "en" ? "en" : "bg";
  const t = TX[lang];

  const [statusOptions, setStatusOptions] = useState(null);
  const [profile, setProfile] = useState(null);
  const [targets, setTargets] = useState(null);
  const [checklist, setChecklist] = useState(null);
  const [saving, setSaving] = useState(false);
  const [openSection, setOpenSection] = useState(null);

  // Form state — synced with `profile` on first load
  const [form, setForm] = useState({
    status: "none",
    mastitis_active: false,
    mastitis_fever_celsius: "",
    mastitis_redness_cm: "",
    mastitis_duration_hours: "",
    breastfeeds_per_day: "",
    comorbidities: [],
    pre_pregnancy_weight_kg: "",
    current_weight_kg: "",
    height_cm: "",
  });

  // ── Bootstrap ──
  useEffect(() => {
    let cancelled = false;
    Promise.all([
      axios.get(`${API}/lactation/status-options`).then((r) => r.data).catch(() => null),
      axios.get(`${API}/lactation/profile`).then((r) => r.data).catch(() => null),
    ]).then(([opts, prof]) => {
      if (cancelled) return;
      setStatusOptions(opts);
      setProfile(prof);
      if (prof) {
        setForm((f) => ({
          ...f,
          status: prof.status || "none",
          mastitis_active: !!prof.mastitis_active,
          mastitis_fever_celsius: prof.mastitis_fever_celsius ?? "",
          mastitis_redness_cm: prof.mastitis_redness_cm ?? "",
          mastitis_duration_hours: prof.mastitis_duration_hours ?? "",
          breastfeeds_per_day: prof.breastfeeds_per_day ?? "",
          comorbidities: prof.comorbidities || [],
          pre_pregnancy_weight_kg: prof.pre_pregnancy_weight_kg ?? "",
          current_weight_kg: prof.current_weight_kg ?? "",
          height_cm: prof.height_cm ?? "",
        }));
      }
    });
    return () => { cancelled = true; };
  }, [API]);

  // ── Fetch targets + checklist whenever profile changes status ──
  useEffect(() => {
    if (!profile || profile.status === "none") {
      setTargets(null);
      setChecklist(null);
      return;
    }
    let cancelled = false;
    Promise.all([
      axios.get(`${API}/lactation/targets`).then((r) => r.data).catch(() => null),
      axios.get(`${API}/lactation/mastitis-checklist`).then((r) => r.data).catch(() => null),
    ]).then(([tg, cl]) => {
      if (cancelled) return;
      setTargets(tg);
      setChecklist(cl);
    });
    return () => { cancelled = true; };
  }, [API, profile]);

  // ── Handlers ──
  const saveProfile = async () => {
    setSaving(true);
    try {
      const payload = {
        status: form.status,
        mastitis_active: !!form.mastitis_active,
        mastitis_fever_celsius: form.mastitis_fever_celsius ? Number(form.mastitis_fever_celsius) : null,
        mastitis_redness_cm: form.mastitis_redness_cm ? Number(form.mastitis_redness_cm) : null,
        mastitis_duration_hours: form.mastitis_duration_hours ? Number(form.mastitis_duration_hours) : null,
        breastfeeds_per_day: form.breastfeeds_per_day ? Number(form.breastfeeds_per_day) : null,
        comorbidities: form.comorbidities || [],
        pre_pregnancy_weight_kg: form.pre_pregnancy_weight_kg ? Number(form.pre_pregnancy_weight_kg) : null,
        current_weight_kg: form.current_weight_kg ? Number(form.current_weight_kg) : null,
        height_cm: form.height_cm ? Number(form.height_cm) : null,
      };
      const { data } = await axios.post(`${API}/lactation/profile`, payload);
      setProfile(data);
      toast.success(lang === "bg" ? "Запазено!" : "Saved!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || (lang === "bg" ? "Грешка при запазване" : "Save error"));
    } finally {
      setSaving(false);
    }
  };

  const toggleComorbidity = (val) => {
    setForm((f) => {
      const set = new Set(f.comorbidities);
      if (set.has(val)) set.delete(val); else set.add(val);
      return { ...f, comorbidities: Array.from(set) };
    });
  };

  // ── Theme ──
  const bgClass = isDark ? "bg-[hsl(223,47%,6%)]" : "bg-slate-50";
  const cardCls = isDark
    ? "bg-[hsl(223,47%,9%)] border-[hsl(223,47%,16%)]"
    : "bg-white border-slate-100 shadow-sm";
  const textColor = isDark ? "text-white" : "text-slate-900";
  const subTextColor = isDark ? "text-slate-400" : "text-slate-600";

  const hasProfile = profile?.status && profile.status !== "none";
  const needsDoctor = !!checklist?.needs_doctor_now;

  return (
    <div className={`min-h-screen ${bgClass}`} data-testid="lactation-coach-page">
      <LactobotFloatingButton lang={lang} />

      <div className="max-w-5xl mx-auto px-4 py-8 sm:py-10">
        {/* Header */}
        <div className="flex items-start gap-3 mb-6">
          <div className="w-12 h-12 rounded-2xl flex items-center justify-center bg-gradient-to-br from-rose-400 to-pink-600 shadow-lg shadow-pink-500/20">
            <Baby className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className={`text-2xl sm:text-3xl font-bold ${textColor}`}>{t.title}</h1>
            <p className={`text-xs sm:text-sm mt-1 ${subTextColor}`}>{t.subtitle}</p>
          </div>
        </div>

        {/* RED FLAG banner — when needs_doctor_now */}
        {needsDoctor && (
          <div
            data-testid="lactation-red-flag-banner"
            className="mb-6 rounded-2xl border-2 border-red-500 bg-red-500/10 p-5"
          >
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-7 h-7 text-red-500 shrink-0 mt-0.5" />
              <div className="flex-1">
                <h2 className="text-lg font-bold text-red-500 mb-1">{t.red_flag_now}</h2>
                <p className={`text-sm mb-3 ${isDark ? "text-red-200" : "text-red-700"}`}>{t.red_flag_explain}</p>
                <ul className={`text-xs space-y-1 mb-3 ${isDark ? "text-red-200" : "text-red-700"}`}>
                  {(checklist?.red_flag_reasons_bg || []).filter(Boolean).map((r, i) => (
                    <li key={i}>• {r}</li>
                  ))}
                </ul>
                <a
                  href="tel:112"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-600 hover:bg-red-700 text-white text-sm font-bold"
                  data-testid="lactation-call-112-btn"
                >
                  <Phone className="w-4 h-4" /> {t.call_112}
                </a>
              </div>
            </div>
          </div>
        )}

        {/* Persona form */}
        <section className={`rounded-2xl border p-5 mb-6 ${cardCls}`} data-testid="lactation-persona-form">
          <h2 className={`text-base font-bold mb-4 flex items-center gap-2 ${textColor}`}>
            <Activity className="w-4 h-4" /> {t.persona_title}
          </h2>

          {/* Status */}
          <label className={`block text-sm font-semibold mb-2 ${subTextColor}`}>{t.status_label}</label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-5">
            {(statusOptions?.statuses || []).map((opt) => {
              const active = form.status === opt.value;
              return (
                <button
                  key={opt.value}
                  onClick={() => setForm((f) => ({ ...f, status: opt.value }))}
                  data-testid={`lactation-status-${opt.value}`}
                  className={`text-left px-3 py-2 rounded-lg text-xs sm:text-sm border transition-all ${
                    active
                      ? "border-pink-500 bg-pink-500/10 text-pink-600 font-semibold"
                      : (isDark
                          ? "border-slate-700 hover:border-slate-500 text-slate-300"
                          : "border-slate-200 hover:border-slate-400 text-slate-700")
                  }`}
                >
                  {lang === "bg" ? opt.label_bg : opt.label_en}
                </button>
              );
            })}
          </div>

          {/* Comorbidities */}
          <label className={`block text-sm font-semibold mb-2 ${subTextColor}`}>{t.comorbidities_label}</label>
          <div className="flex flex-wrap gap-2 mb-5">
            {(statusOptions?.comorbidities || []).map((c) => {
              const active = form.comorbidities.includes(c.value);
              return (
                <button
                  key={c.value}
                  onClick={() => toggleComorbidity(c.value)}
                  data-testid={`lactation-comorbidity-${c.value}`}
                  className={`px-3 py-1.5 rounded-full text-xs border transition-all ${
                    active
                      ? "border-violet-500 bg-violet-500/10 text-violet-600 font-semibold"
                      : (isDark
                          ? "border-slate-700 hover:border-slate-500 text-slate-300"
                          : "border-slate-200 hover:border-slate-400 text-slate-700")
                  }`}
                >
                  {lang === "bg" ? c.label_bg : c.label_en}
                </button>
              );
            })}
          </div>

          {/* Body metrics */}
          <label className={`block text-sm font-semibold mb-2 ${subTextColor}`}>{t.body_label}</label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-5">
            <NumberField testId="lactation-weight-pre" label={t.weight_pre} value={form.pre_pregnancy_weight_kg}
              setForm={setForm} field="pre_pregnancy_weight_kg" isDark={isDark} />
            <NumberField testId="lactation-weight-now" label={t.weight_kg} value={form.current_weight_kg}
              setForm={setForm} field="current_weight_kg" isDark={isDark} />
            <NumberField testId="lactation-height" label={t.height_cm} value={form.height_cm}
              setForm={setForm} field="height_cm" isDark={isDark} />
            <NumberField testId="lactation-feeds" label={t.breastfeeds} value={form.breastfeeds_per_day}
              setForm={setForm} field="breastfeeds_per_day" isDark={isDark} />
          </div>

          {/* Mastitis */}
          <div className={`rounded-xl border p-4 ${isDark ? "border-slate-700 bg-slate-900/40" : "border-slate-200 bg-slate-50"}`}>
            <h3 className={`text-sm font-bold mb-3 flex items-center gap-2 ${textColor}`}>
              <Flame className="w-4 h-4 text-orange-500" /> {t.mastitis_title}
            </h3>
            <label className="flex items-center gap-2 cursor-pointer mb-3">
              <input
                type="checkbox"
                checked={!!form.mastitis_active}
                onChange={(e) => setForm({ ...form, mastitis_active: e.target.checked })}
                data-testid="lactation-mastitis-toggle"
              />
              <span className={`text-sm ${textColor}`}>{t.mastitis_active_label}</span>
            </label>
            {form.mastitis_active && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-2">
                <NumberField testId="lactation-mastitis-fever" label={t.mastitis_fever}
                  value={form.mastitis_fever_celsius} setForm={setForm} field="mastitis_fever_celsius"
                  step="0.1" isDark={isDark} />
                <NumberField testId="lactation-mastitis-redness" label={t.mastitis_redness}
                  value={form.mastitis_redness_cm} setForm={setForm} field="mastitis_redness_cm"
                  isDark={isDark} />
                <NumberField testId="lactation-mastitis-hours" label={t.mastitis_hours}
                  value={form.mastitis_duration_hours} setForm={setForm} field="mastitis_duration_hours"
                  isDark={isDark} />
              </div>
            )}
          </div>

          <Button
            onClick={saveProfile}
            disabled={saving || form.status === "none"}
            className="mt-5 bg-pink-600 hover:bg-pink-700 text-white"
            data-testid="lactation-save-profile-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            {t.save}
          </Button>
        </section>

        {/* Daily targets */}
        {hasProfile && targets && (
          <DailyTargetsCard t={t} lang={lang} targets={targets.targets} cardCls={cardCls}
            textColor={textColor} subTextColor={subTextColor} isDark={isDark} />
        )}

        {/* Mastitis checklist */}
        {hasProfile && checklist?.active && (
          <MastitisChecklist t={t} lang={lang} items={checklist.checklist}
            disclaimer={checklist.medical_disclaimer_bg}
            cardCls={cardCls} textColor={textColor} subTextColor={subTextColor} isDark={isDark} />
        )}

        {/* Food picks (always visible — educational) */}
        {hasProfile && (
          <FoodPicksGrid t={t} lang={lang} cardCls={cardCls} textColor={textColor}
            subTextColor={subTextColor} isDark={isDark}
            openSection={openSection} setOpenSection={setOpenSection} />
        )}

        {!hasProfile && (
          <div className={`rounded-2xl border p-6 text-center ${cardCls}`}>
            <Sparkles className={`w-8 h-8 mx-auto mb-3 ${subTextColor}`} />
            <p className={`text-sm ${subTextColor}`}>{t.save_profile_first}</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────── Sub-components ───────────────

function NumberField({ label, value, setForm, field, step = "1", isDark, testId }) {
  return (
    <label className="block">
      <span className={`block text-[10px] uppercase tracking-wider mb-1 ${isDark ? "text-slate-400" : "text-slate-600"}`}>
        {label}
      </span>
      <input
        type="number"
        step={step}
        value={value}
        onChange={(e) => setForm((f) => ({ ...f, [field]: e.target.value }))}
        data-testid={testId}
        className={`w-full px-3 py-2 rounded-lg text-sm border ${
          isDark
            ? "bg-slate-900 border-slate-700 text-white"
            : "bg-white border-slate-300 text-slate-900"
        }`}
      />
    </label>
  );
}

function DailyTargetsCard({ t, lang, targets, cardCls, textColor, subTextColor, isDark }) {
  const rows = useMemo(() => [
    { id: "kcal", label_bg: "Калории", label_en: "Calories", value: targets.kcal, unit: "kcal", icon: Flame, color: "#F97316" },
    { id: "protein", label_bg: "Протеин", label_en: "Protein", value: targets.protein_g, unit: "g", icon: Activity, color: "#0EA5E9" },
    { id: "fat", label_bg: "Мазнини", label_en: "Fat", value: targets.fat_g, unit: "g", icon: Droplets, color: "#EAB308" },
    { id: "carbs", label_bg: "Въглехидрати", label_en: "Carbs", value: targets.carbs_g, unit: "g", icon: Salad, color: "#10B981" },
    { id: "fiber", label_bg: "Фибри", label_en: "Fiber", value: targets.fiber_g, unit: "g", icon: Salad, color: "#22C55E" },
    { id: "dha", label_bg: "DHA (omega-3)", label_en: "DHA (omega-3)", value: targets.dha_mg, unit: "mg", icon: Heart, color: "#EC4899" },
    { id: "water", label_bg: "Вода", label_en: "Water", value: targets.water_ml, unit: "ml", icon: Droplets, color: "#06B6D4" },
    targets.lecithin_mg > 0 && { id: "lecithin", label_bg: "Лецитин", label_en: "Lecithin", value: targets.lecithin_mg, unit: "mg", icon: ShieldCheck, color: "#8B5CF6" },
    { id: "iodine", label_bg: "Йод", label_en: "Iodine", value: targets.iodine_ug, unit: "µg", icon: Sparkles, color: "#3B82F6" },
    { id: "iron", label_bg: "Желязо", label_en: "Iron", value: targets.iron_mg, unit: "mg", icon: Activity, color: "#DC2626" },
    // iter530c — Daily DIL & NIS estimates (only shown when defined)
    targets.dil_g !== undefined && targets.dil_g !== null && {
      id: "dil", label_bg: "DIL (инсулиногенен товар)", label_en: "DIL (insulinogenic load)",
      value: targets.dil_g, unit: "g", icon: Brain, color: "#A855F7",
    },
    targets.nis_percent !== undefined && targets.nis_percent !== null && {
      id: "nis", label_bg: "NIS (инсулинов отзив)", label_en: "NIS (insulin response)",
      value: targets.nis_percent, unit: "%", icon: ShieldCheck, color: "#EF4444",
    },
  ].filter(Boolean), [targets]);

  return (
    <section className={`rounded-2xl border p-5 mb-6 ${cardCls}`} data-testid="lactation-daily-targets">
      <h2 className={`text-base font-bold mb-1 flex items-center gap-2 ${textColor}`}>
        <Brain className="w-4 h-4" /> {t.targets_title}
      </h2>
      <p className={`text-[11px] mb-4 ${subTextColor}`}>{t.targets_disclaimer}</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {rows.map((r) => {
          const Icon = r.icon;
          return (
            <div
              key={r.id}
              data-testid={`lactation-target-${r.id}`}
              className={`p-3 rounded-xl ${isDark ? "bg-slate-900/40" : "bg-slate-50"}`}
            >
              <div className="flex items-center gap-1.5 mb-1">
                <Icon className="w-3.5 h-3.5" style={{ color: r.color }} />
                <span className={`text-[10px] uppercase tracking-wider ${subTextColor}`}>
                  {lang === "bg" ? r.label_bg : r.label_en}
                </span>
              </div>
              <div className={`text-xl font-bold ${textColor}`}>
                {r.value} <span className={`text-xs font-normal ${subTextColor}`}>{r.unit}</span>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function MastitisChecklist({ t, lang, items, disclaimer, cardCls, textColor, subTextColor, isDark }) {
  const [checked, setChecked] = useState({});
  const toggle = (id) => setChecked((c) => ({ ...c, [id]: !c[id] }));
  return (
    <section className={`rounded-2xl border p-5 mb-6 ${cardCls}`} data-testid="lactation-mastitis-checklist">
      <h2 className={`text-base font-bold mb-3 flex items-center gap-2 ${textColor}`}>
        <Flame className="w-4 h-4 text-orange-500" /> {t.checklist_title}
      </h2>
      <ul className="space-y-2 mb-4">
        {(items || []).map((it) => (
          <li
            key={it.id}
            className={`flex items-start gap-3 p-2.5 rounded-lg cursor-pointer transition-colors ${
              checked[it.id]
                ? (isDark ? "bg-emerald-500/10" : "bg-emerald-50")
                : (isDark ? "hover:bg-slate-800" : "hover:bg-slate-50")
            }`}
            onClick={() => toggle(it.id)}
            data-testid={`lactation-checklist-${it.id}`}
          >
            <input
              type="checkbox"
              checked={!!checked[it.id]}
              onChange={() => toggle(it.id)}
              className="mt-0.5"
              onClick={(e) => e.stopPropagation()}
            />
            <span className={`text-sm ${checked[it.id] ? "line-through opacity-60" : ""} ${textColor}`}>
              {lang === "bg" ? it.label_bg : it.label_en}
            </span>
          </li>
        ))}
      </ul>
      {disclaimer && (
        <div className={`text-[11px] p-3 rounded-lg ${isDark ? "bg-red-500/10 text-red-300" : "bg-red-50 text-red-700"}`}>
          {disclaimer}
        </div>
      )}
    </section>
  );
}

function FoodPicksGrid({ t, lang, cardCls, textColor, subTextColor, isDark, openSection, setOpenSection }) {
  const sections = [
    { id: "galactogogue", title: t.galactogogue_section, icon: Salad,        color: "#10B981", items: FOOD_CATALOG.galactogogue },
    { id: "lecithin",     title: t.lecithin_section,     icon: ShieldCheck,  color: "#8B5CF6", items: FOOD_CATALOG.lecithin },
    { id: "anti_inflam",  title: t.anti_inflam_section,  icon: Heart,        color: "#EC4899", items: FOOD_CATALOG.anti_inflam },
    { id: "caution",      title: t.caution_section,      icon: AlertTriangle, color: "#EF4444", items: FOOD_CATALOG.caution },
  ];
  return (
    <section className={`rounded-2xl border p-5 mb-6 ${cardCls}`} data-testid="lactation-food-picks">
      <h2 className={`text-base font-bold mb-4 flex items-center gap-2 ${textColor}`}>
        <BookOpen className="w-4 h-4" /> {t.food_picks_title}
      </h2>
      <div className="space-y-2">
        {sections.map((s) => {
          const Icon = s.icon;
          const isOpen = openSection === s.id || openSection === "all";
          return (
            <div key={s.id} className={`rounded-xl border ${isDark ? "border-slate-700" : "border-slate-200"}`}>
              <button
                onClick={() => setOpenSection(isOpen ? null : s.id)}
                data-testid={`lactation-foodpicks-${s.id}-toggle`}
                className={`w-full flex items-center gap-3 p-3 text-left ${textColor}`}
              >
                <Icon className="w-4 h-4 shrink-0" style={{ color: s.color }} />
                <span className="text-sm font-semibold flex-1">{s.title}</span>
                <span className={`text-[10px] ${subTextColor}`}>{s.items.length}</span>
                {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              </button>
              {isOpen && (
                <div className={`px-3 pb-3 space-y-1.5 ${subTextColor}`}>
                  {s.items.map((it, i) => (
                    <div key={i} className={`text-xs px-2 py-1.5 rounded-lg ${isDark ? "bg-slate-900/40" : "bg-slate-50"}`}>
                      <div className="flex items-baseline justify-between gap-2 flex-wrap">
                        <span className={`font-medium ${textColor}`}>{lang === "bg" ? it.name_bg : it.name_en}</span>
                        {it.tier && (
                          <span style={{ color: TIER_COLOR[it.tier] }} className="text-[10px] font-bold uppercase">
                            {it.tier}
                          </span>
                        )}
                        {it.mg && (
                          <span className={`text-[10px] ${subTextColor}`}>{it.mg}</span>
                        )}
                      </div>
                      {(it.note_bg || it.note_en) && (
                        <div className={`text-[11px] mt-0.5 italic ${subTextColor}`}>
                          {lang === "bg" ? (it.note_bg || it.note_en) : (it.note_en || it.note_bg)}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
